import OccurrenceTypesModal from './OccurrenceTypesModal';
import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import DeleteModal from '../../../components/DeleteModal';
import { Pagination } from '../../../components/Pagination';
import { dispatchService } from '../../../services/central-api/dispatch';
import { ClientError } from '../../../services/central-api/base-service';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { RootNavigation } from '../../../Routes';

export default function OccurrenceTypes({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<DispatchParamList, 'OccurrenceTypes'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['OccurrenceTypes']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedOccurrenceType, setSelectedOccurrenceType] = useState<OccurrenceType>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getOccurrenceTypes', filters],
        queryFn: () => dispatchService.getOccurrenceTypes(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function confirmDelete(occurrenceType: OccurrenceType) {
        try {
            if (occurrenceType?.id) {
                await dispatchService.deleteOccurrenceType(occurrenceType.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setDeleteModalVisible(false);
                refetch();

            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFormClose() {
        setSelectedOccurrenceType(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('occurrence_types')}
            actions={
                <CustomButton
                    text={translate('register')}
                    onPress={() => {
                        setSelectedOccurrenceType(undefined);
                        setModalVisible(true);
                    }}
                />
            }
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredOccurrencesTypes')}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter} />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />

            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: 'ID',
                            selector: row => row.id,
                            grow: 0
                        },
                        {
                            name: translate('name'),
                            selector: row => row.name,
                            wrap: true,
                            grow: 2
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.actingBody.name,
                            wrap: true,
                        },
                        {
                            cell: row => (
                                <DropdownButton
                                    actions={[
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedOccurrenceType(row);
                                                setModalVisible(true);
                                            }
                                        },
                                        {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedOccurrenceType(row);
                                                setDeleteModalVisible(true);
                                            },
                                        }
                                    ]}
                                />
                            ),
                            wrap: true,
                            button: true
                        }
                    ]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>



        <OccurrenceTypesModal
            onClose={handleFormClose}
            isModalVisible={isModalVisible}
            occurrenceType={selectedOccurrenceType}
        />

        {selectedOccurrenceType &&
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedOccurrenceType.name}
                onSubmit={async () => {
                    await confirmDelete(selectedOccurrenceType);
                }}
            />
        }

    </Container >;
}
